// The cast handlers: what a resolved rune expression actually does to the world.
//
// Moved out of GameScene (2026-07-30, sim extraction step 4b -- the last big
// move). These are the operator effects ui.ts triggers after a cast resolves:
// Transfer moves a quantity between two loci, Transfer·Momentum shoves an
// adjacent object and recoils the caster, Project pushes a quantity (or a
// force) outward from the caster along their facing, Separate·Mass splits a
// rock in two (Reverse: merges two into one), and Bind·Momentum couples an
// object's own movement to a marked Source (or the caster; Separate·Momentum
// releases it).
//
// The logic is unchanged from the scene versions. What changed is the three
// things each of them used to reach for directly: `this.tileX`/`this.facing` are
// now `world.player`, `updateStatsHud()`/`movePlayerTo()`'s tween/
// `animateProjectFx()` are now SimEvents (see events.ts), and playerStats.ts's
// draws are now player.ts's. Nothing here knows a renderer exists.
//
// The `out` array is the whole seam. A caller with a renderer (GameScene's
// applySimEvents) walks it and animates; a headless caller -- a unit test, the
// world editor -- can ignore it entirely and just look at the world afterward.
import { WorldState } from './state';
import { ObjectDef, Facing } from './types';
import { SimEvents } from './events';
import { ConceptId } from '../runes/concepts';
import { DIRECTIONS, DIRECTION_OFFSETS, ROCK_TIER_META } from './constants';
import { atScale } from './physics';
import * as Q from './queries';
import { addObjectToRoom, checkMeltThreshold, removeObjectFromRoom } from './mechanics';
import { settleWorld } from './settle';
import { displacedEntropyLocus } from './entropyBind';
import { conductFloorContact } from './chargeBind';
import { damagePlayer, drawCharge, drawMana, forceDrawMana, getCharge, getMana, giveCharge, drawThermal, giveThermal } from './player';

// ---------------------------------------------------------------------------
// What a cast is asked to do, and what it reports back.
//
// These shapes were declared in game/src/{transfer,momentum,project,separate}
// Effect.ts, which are the pub/sub bridges from ui.ts's cast handler to whoever
// applies the effect. The *bridges* are still there and unchanged -- what moved
// here is only the vocabulary, because the functions below are now what speaks
// it. Those modules re-export these, so every existing import site is untouched.
// ---------------------------------------------------------------------------

export interface TransferEffectEvent {
  conceptId: ConceptId;
  sourceObjectId: string;
  targetObjectId: string;
  /** True for the Reverse modifier -- reversed here means "pull from the target
   * into the source" instead of the usual "from source into the target". */
  reversed: boolean;
  /** magnitudeScale()'s scale (1/10/100), reused so the world effect's size tracks
   * the same Precise/Standard/Unbound the player actually chose. */
  scale: number;
}

export interface TransferEffectResult {
  applied: boolean;
  meltedTargetName?: string;
  /** What any state ladder this cast moved says happened -- see
   * `HeatStateStage.narrates`. Collected from the events the melt checks just
   * pushed rather than threaded back through `checkMeltThreshold`'s own return,
   * which is a boolean every one of its ~20 call sites reads as one. */
  stateNotes?: string[];
  meltedSourceName?: string;
  /** Never set by applyTransferEffect today -- neither Energy (thermalState
   * has its own danger model, not a forced draw, see drawThermal/giveThermal
   * in player.ts) nor Charge (drawCharge never forces) ever costs Health here
   * anymore. Kept on the type rather than removed: applyMomentumEffect's own
   * result still uses this exact shape for its real forced-Mana-draw case,
   * and a future locus that does force a shortfall would want the same
   * field, not a second one. */
  healthCost?: number;
  /** Set when this Transfer relocated a *generator* rather than moving Charge:
   * a marked source carrying `maxProperties.charge`, cast at a faced object
   * with `sourceMount`. Names what moved and where, so ui.ts can narrate an
   * installation rather than a trickle. See applyTransferEffect. */
  relocatedSource?: { sourceName: string; targetName: string };
  /** When a `Bind`·`Entropy` link displaced the cast, the object it actually
   * landed on / was drawn from -- absent when the cast acted where it was
   * aimed. See `sim/entropyBind.ts`. */
  displacedTargetName?: string;
  displacedSourceName?: string;
  /** How much actually crossed. Zero is a real outcome rather than a failure --
   * an empty source gives nothing -- and it is the difference between "the link
   * moved this somewhere" and "the far end had nothing to give". */
  moved?: number;
  /** True whenever the *actual* source ended up being the caster -- either
   * because none was marked, or because a marked source turned out to be a
   * different room's object (see applyTransferEffect) and fell back to Self.
   * ui.ts uses this rather than its own naive "is the marked id 'self'" check,
   * so the narration credits whoever the quantity actually came from, not a
   * stale mark that silently didn't apply. */
  sourceWasSelf?: boolean;
}

export interface MomentumEffectEvent {
  targetObjectId: string;
  /** magnitudeScale()'s scale (1/10/100), reused so a bigger push costs more
   * and moves the object further, same as Energy/Charge Transfer's amount. */
  scale: number;
}

export interface MomentumEffectResult {
  applied: boolean;
  tilesRequested: number;
  tilesMoved: number;
  /** Set when the caster's own Mana came up short of a forced Unbound push --
   * see player.ts's forceDrawMana. The value is the Health lost. */
  healthCost?: number;
}

export interface ProjectEffectEvent {
  conceptId: ConceptId;
  /** true = Radial (every direction, within range); false = Linear (straight
   * ahead along the caster's current facing only). */
  radial: boolean;
  /** magnitudeScale()'s scale (1/10/100) -- reused for both the amount delivered
   * and how far the projection reaches, same convention as radialMeasure's
   * Precise/Standard/Unbound -> 3/6/10 tile radius. */
  scale: number;
  /** Momentum: false = a force-push beam/field (shoves every pushable object
   * it reaches away from the caster), true = a tractor beam (pulls them in
   * instead). Energy: false = a heat-ray (raises each target's Energy),
   * true = a cold-ray (lowers it by the same amount instead) -- both are the
   * same "invert whatever directionality this cast would otherwise default
   * to" reading Reverse already has under Transfer/Entropy. Unused for
   * Charge and every other concept today. */
  reversed: boolean;
}

export interface ProjectEffectResult {
  applied: boolean;
  /** Every in-range object with `properties` that received the projected
   * quantity, whether or not it crossed a meltsWhen threshold. For Momentum,
   * every in-range *pushable* object the beam/field reached, whether or not
   * it actually budged. */
  hitNames: string[];
  /** Subset of hitNames whose meltsWhen threshold was crossed as a result. */
  meltedNames: string[];
  /** See `TransferEffectResult.stateNotes`. */
  stateNotes?: string[];
  /** True when the cast didn't fire at all because the caster's own reserve
   * (Energy, or Charge) came up short of the full nominal cost -- v1
   * deliberately has no partial-delivery path for Project (unlike Transfer):
   * either the full amount goes out, or nothing does, and this flag is what
   * lets ui.ts narrate that distinctly from "nothing was in range." See
   * applyProjectEffect below and 12_Mechanics_World_Interaction.md. */
  insufficientReserve?: boolean;
  /** True when the caster has no reserve of this concept *at all* to project
   * from -- a different refusal from `insufficientReserve`, which means they
   * have one and it is too low. See `projectReserveFor`. */
  noReserve?: boolean;
  /** Momentum only: subset of hitNames that actually moved at least one tile
   * -- some may have been wedged against a wall or another object already in
   * the direction they'd be pushed/pulled, catching the field but not moving. */
  movedNames?: string[];
}

/** Which of the caster's own reserves funds a projection of this concept, or
 * `null` if they have none to fund it with.
 *
 * **Project throws something the caster has.** `PlayerState` holds exactly two
 * things a cast can spend -- Mana (the body's own Energy) and Charge -- plus
 * Health, which is a cost of last resort rather than a source. Energy projects
 * from Mana and Charge from Charge; Momentum projects from Mana too, because a
 * shove is *effort* rather than substance and the caster has no reservoir of
 * momentum to empty. Entropy, Mass, Space and Time have neither: there is no
 * locus in the caster to draw them out of, so a projection of one has nothing
 * to be made of and does not happen.
 *
 * **Declared rather than derived from an `else`, which is the whole point.**
 * This used to be the false branch of `isEnergy ? drawMana : drawCharge`, so
 * every concept that was not Energy silently drew from the *Charge* pool --
 * making `Project`·`Entropy` a working ranged Entropy weapon funded by an
 * unrelated reserve, with no sufficiency check in front of it, and the same for
 * Mass, Space and Time. None of the four was ever meant to be a valid cast.
 *
 * A table rather than four refusals also keeps this on the right side of
 * `CLAUDE.md`'s standing rule: it extends what `Project` *means* -- you can only
 * project what you are carrying -- rather than blacklisting particular
 * concept/operator pairs. A concept added later is refused until somebody
 * decides what the caster would be spending, which is the correct default. */
export function projectReserveFor(conceptId: ConceptId): 'mana' | 'charge' | null {
  if (conceptId === 'energy' || conceptId === 'momentum') return 'mana';
  if (conceptId === 'charge') return 'charge';
  return null;
}

export interface SeparateEffectEvent {
  targetObjectId: string;
  /** The Reverse modifier -- false splits the target into two smaller rocks,
   * true instead merges it with an adjacent same-tier rock into one bigger
   * one. */
  reversed: boolean;
}

export type SeparateFailureReason = 'not-a-rock' | 'too-small-to-split' | 'no-room-to-split' | 'already-whole' | 'no-partner-to-merge' | 'warded';

export interface SeparateEffectResult {
  applied: boolean;
  /** Name of whatever the player actually faced -- always known, even on
   * failure, so ui.ts can narrate why nothing happened. */
  targetName: string;
  /** Set when applied: true -- the name of the tier actually produced (e.g.
   * "Rock" after a Boulder splits, "Boulder" after two Rocks merge). */
  resultName?: string;
  /** Set when applied: false -- which specific constraint stopped it. */
  failureReason?: SeparateFailureReason;
}

export interface BindEffectEvent {
  targetObjectId: string;
  /** The marked Source, same resolution Transfer already uses -- `'self'`
   * when nothing is marked. */
  sourceObjectId: string;
  /** The Reverse modifier, captured at bind time -- see BindDef.mirrored. */
  mirrored: boolean;
}

export interface BindEffectResult {
  applied: boolean;
  /** Name of whatever the player actually faced -- known even on failure, so
   * ui.ts can narrate why nothing happened. */
  targetName: string;
  /** True if the link actually ended up caster-sourced -- either because
   * nothing was marked, or because a marked source turned out to be a stale
   * cross-room reference (see applyTransferEffect's own sourceWasSelf for the
   * same fallback). */
  sourceWasSelf: boolean;
}

export interface UnbindEffectEvent {
  targetObjectId: string;
}

export interface UnbindEffectResult {
  applied: boolean;
  targetName: string;
}

export interface EnergyBindEffectEvent {
  targetObjectId: string;
  /** The marked Source, same resolution Transfer/Bind·Momentum already use --
   * `'self'` when nothing is marked. */
  sourceObjectId: string;
  /** The Reverse modifier, captured at bind time -- see EnergyBindDef.mirrored. */
  mirrored: boolean;
}

export interface EnergyBindEffectResult {
  applied: boolean;
  targetName: string;
  /** True if the link actually ended up caster-sourced -- either because
   * nothing was marked, or because a marked source turned out to be a stale
   * cross-room reference. Same fallback shape applyBindEffect's own
   * sourceWasSelf already has. */
  sourceWasSelf: boolean;
}

export interface UnbindEnergyEffectEvent {
  targetObjectId: string;
}

export interface UnbindEnergyEffectResult {
  applied: boolean;
  targetName: string;
}

export interface ChargeBindEffectEvent {
  targetObjectId: string;
  /** The marked Source, same resolution every other Bind reading already uses
   * -- `'self'` when nothing is marked. */
  sourceObjectId: string;
  /** The Reverse modifier, captured at bind time -- see ChargeBindDef.mirrored. */
  mirrored: boolean;
}

export interface ChargeBindEffectResult {
  applied: boolean;
  targetName: string;
  /** True if the wire actually ended up caster-sourced -- either because
   * nothing was marked, or because a marked source turned out to be a stale
   * cross-room reference. Same fallback shape applyBindEffect's own
   * sourceWasSelf already has. */
  sourceWasSelf: boolean;
}

export interface UnbindChargeEffectEvent {
  targetObjectId: string;
}

export interface UnbindChargeEffectResult {
  applied: boolean;
  targetName: string;
}

export interface EntropyBindEffectEvent {
  targetObjectId: string;
  /** The marked Source, same resolution every other Bind reading already uses
   * -- `'self'` when nothing is marked. */
  sourceObjectId: string;
  /** The Reverse modifier, captured at bind time -- see EntropyBindDef.mirrored. */
  mirrored: boolean;
}

export interface EntropyBindEffectResult {
  applied: boolean;
  targetName: string;
  /** True if the link ended up caster-sourced -- either because nothing was
   * marked, or because a marked source turned out to be a stale cross-room
   * reference. Same fallback shape every other Bind's own `sourceWasSelf`
   * has, but with a consequence the others do not have: the caster holds no
   * Entropy at all, so a caster-sourced link is inert. `settleEntropyBind`
   * skips it, and ui.ts narrates it as the nothing it is. */
  sourceWasSelf: boolean;
  /** True when both loci carry a `properties.entropy` value, i.e. the link
   * will actually do something. False for a caster-sourced link, and for a
   * partner that takes no part in Entropy -- the same "no reserve, no edge"
   * guard `chargeComponents` applies to a wire. Reported rather than inferred
   * from `sourceWasSelf` alone so a caller can tell the two silent cases
   * apart. */
  coupled: boolean;
}

export interface UnbindEntropyEffectEvent {
  targetObjectId: string;
}

export interface UnbindEntropyEffectResult {
  applied: boolean;
  targetName: string;
}

// ---------------------------------------------------------------------------
// Movement and the world-scoped wrappers the casts below share.
// ---------------------------------------------------------------------------

// Shared by tryMove (single-tile, input-driven) and applyMomentumEffect
// (multi-tile recoil from a Transfer + Momentum cast) -- both end with the
// same "update the player's tile, glide the sprite there" step. The glide is
// the renderer's half of the 'player-moved' event. Every real position
// change made here also drags along whatever is currently Bind·Momentum-
// linked to the caster (see propagateBoundMovement) -- one choke point
// covers an ordinary step and a momentum recoil alike, rather than each cast
// site remembering to propagate it. It is also the one choke point a live
// Metal Floor hazard is checked against (see applyFloorHazard below), for the
// same reason: whatever got the caster here -- an ordinary step, a recoil --
// crossing onto a live tile costs the same either way.
export function movePlayerTo(world: WorldState, x: number, y: number, out: SimEvents): void {
  if (x === world.player.x && y === world.player.y) return;
  const dx = x - world.player.x;
  const dy = y - world.player.y;
  world.player.x = x;
  world.player.y = y;
  out.push({ kind: 'player-moved', x, y });
  // A shock moves real Charge (see applyFloorHazard), which every conduit gate
  // downstream of that network reads.
  applyFloorHazard(world, x, y, out);
  propagateBoundMovement(world, 'self', dx, dy, out);
  // Unconditional, where this used to settle only if the step conducted or
  // dragged something (2026-08-08). The caster's own position became world
  // state a system reads the moment `pad.forPlayer` existed -- a step onto a
  // player pad mutates nothing else at all, so the two old triggers would both
  // read false and the gate wired to it would never notice. Which systems a
  // mutation invalidates is settle.ts's judgement to make, not this call
  // site's (see sim/settle.ts's own header), and "the caster moved" is a
  // mutation like any other. Affordable for the same reason settleWorld is
  // called unconditionally everywhere else: every system is a pure recompute
  // over one room, and a step is already the cheapest thing the player can do.
  settleWorld(world, out);
}

// An unconditional hazard (Lava) with no "live" state at all burns whoever
// steps onto it; a still-conductive Metal Floor tile *conducts* into them, and
// the conduction is the damage. Checked once, right after the position
// actually changes, against the tile just landed on -- not reapplied for
// merely standing there afterward, since this only runs from inside
// movePlayerTo's own early-return-on-no-change guard above. Returns whether
// Charge actually moved -- movePlayerTo settles either way now, so this is
// reported for the benefit of a caller that wants to know, not to decide it.
//
// **The shock is an equalization** (2026-08-08, step 7 of the capacity
// rebuild). It used to be a flat `conduit.shockDamage` whenever the tile's
// network read at or above `physics.metal.floorLiveCharge`, which made a
// hazard a switch: on or off, identical every time, and unaffected by anything
// the caster was carrying. Now contact joins the caster to the tile's network
// exactly the way a Bind·Charge wire would (`conductFloorContact`), and the
// bill is the Charge that crossed:
//
//   damage = shockDamage * |crossed| / floorLiveCharge, rounded
//
// so `conduit.shockDamage` keeps its authored meaning as "the Health a
// threshold-sized crossing costs" rather than becoming a raw per-unit rate --
// a Font holding 8 on a capacity-0 grid still hands over exactly 4 and still
// costs exactly 3, which is what every shipped hazard is authored against.
//
// Two gates, and they are different questions on purpose. `isConduitLive` asks
// whether the *potential difference* across the contact is worth arcing -- the
// same reading the renderer shows, now measured against the caster's own
// potential so it is symmetric. `conductFloorContact` then answers how much
// Charge that difference actually moves, which depends on what the network can
// store: a grid backed by a Font hands over what the capacity split says, while
// a store-less one shares evenly with the caster in whichever direction the
// gradient runs (see equalizedValues' degeneracy note).
function applyFloorHazard(world: WorldState, x: number, y: number, out: SimEvents): boolean {
  const room = world.rooms[world.player.roomId];
  const objectsHere = room.objects.filter((o) => o.x === x && o.y === y);

  const conduitHazard = objectsHere.find((o) => o.conduit?.shockDamage !== undefined);
  if (!conduitHazard) return false;
  if (!Q.isConduitLive(room, conduitHazard, world.physics, { charge: world.player.charge })) return false;

  const crossed = conductFloorContact(world, conduitHazard);
  if (crossed === 0) return false;

  const damage = Math.round((conduitHazard.conduit!.shockDamage! * Math.abs(crossed)) / world.physics.metal.floorLiveCharge);
  // The Charge moved regardless, so the reserves changed even on a crossing too
  // small to round up to a point of Health.
  if (damage > 0) dealFloorDamage(world, damage, out);
  else out.push({ kind: 'stats-changed' });
  return true;
}

function dealFloorDamage(world: WorldState, amount: number, out: SimEvents): void {
  damagePlayer(world, amount, out);
  out.push({ kind: 'stats-changed' });
  out.push({ kind: 'player-hurt', amount });
}

// Applies a locus's own just-made tile delta (or its negation, for a
// Reverse-bound "mirror" link) to every object currently bound to that exact
// locus -- `'self'` for the caster (from movePlayerTo), or an object's id
// (from moveObjectTo, when the object that just moved is itself a marked
// Bind Source something else follows). Returns whether anything actually
// moved, so a caller that doesn't already unconditionally settle
// (movePlayerTo) can skip it when nothing did.
//
// Deliberately one link deep, not transitive: an object bound to X only ever
// reads X's own movement here, never re-propagates through a *second* bind
// pointed at it in turn -- avoiding both infinite chains from an accidental
// cycle (A bound to B, B bound to A) and the added complexity of resolving an
// arbitrarily deep chain nobody has asked for.
//
// Never lets a bound object land on the exact tile its own locus (caster or
// source object) now occupies -- without this, a mirrored link can compute a
// destination that coincides with where the locus itself just moved to,
// since isWalkable alone has no idea the caster (never one of room.objects)
// or a specific source object is standing there. Also never lets it land on
// the caster's own current tile when the locus is a *different*, marked
// object -- a Reverse-bound link moves opposite its locus's direction, which
// an ordinary push (always away from whoever's pushing) never does, so this
// is the one way a bound object's path can double back through wherever the
// caster happens to be standing. A real, reported bug before this: a
// mirrored Bind·Momentum link visibly dragged a block straight through the
// player instead of stopping at them.
//
// Known simplification: each object's destination is checked against
// `room.objects`' state at the moment its own turn in this loop comes up, not
// against where every other bound object is about to end up -- two bound
// objects sitting adjacent, in the locus's direction of travel, can
// therefore block each other on a step where an ideal simultaneous move
// would have let both through cleanly. Rare enough (it takes two separately
// bound objects placed back-to-back) that this is left as a known quirk
// rather than a two-pass conflict resolver, the same tolerance this game
// already extends to other multi-object edge cases.
function propagateBoundMovement(world: WorldState, sourceLocus: string, dx: number, dy: number, out: SimEvents): boolean {
  if (dx === 0 && dy === 0) return false;
  const room = world.rooms[world.player.roomId];
  const locus = sourceLocus === 'self' ? world.player : room.objects.find((o) => o.id === sourceLocus);
  if (!locus) return false;
  let moved = false;
  for (const obj of room.objects) {
    if (!obj.bind || (obj.bind.sourceId ?? 'self') !== sourceLocus) continue;
    const dest = Q.boundObjectMove(room, obj, locus.x, locus.y, dx, dy, world.physics, world.player.x, world.player.y);
    if (!dest) continue;
    obj.x = dest.x;
    obj.y = dest.y;
    out.push({ kind: 'object-moved', objectId: obj.id, x: dest.x, y: dest.y });
    moved = true;
  }
  return moved;
}

// Updates a pushed object's own logical position (so isWalkable/collision
// reflects it immediately) and reports the move, then re-runs the two rules a
// changed object position can invalidate -- used by both the walk-into-push in
// GameScene.tryMove and applyMomentumEffect's cast-based push. Also the other
// half of propagateBoundMovement's two call sites: if `obj` is itself a
// marked Bind Source, whatever follows it is dragged along by the same delta.
export function moveObjectTo(world: WorldState, obj: ObjectDef, x: number, y: number, out: SimEvents): void {
  const dx = x - obj.x;
  const dy = y - obj.y;
  obj.x = x;
  obj.y = y;
  // The renderer glides the sprite here, and also re-points the marked-source
  // reticle: if the moved object happens to be the marked source, the reticle
  // should follow it rather than staying behind at its old tile.
  out.push({ kind: 'object-moved', objectId: obj.id, x, y });
  propagateBoundMovement(world, obj.id, dx, dy, out);
  // A push may have just settled (or vacated) a room8 pressure pad, or moved an
  // obstacle into or out of room9's water channel (the redirect Boulder). Which
  // systems that invalidates -- and in what order they have to re-run -- is
  // settle.ts's business now, not this function's. Already unconditional, so
  // it covers whatever propagateBoundMovement above just did too.
  settleWorld(world, out);
}

// One ordinary, input-driven step: the whole "what is in the tile ahead, and
// does walking into it move it, follow it, redirect through it, or stop me"
// decision, plus the step itself. Returns whether the caster actually moved.
//
// Lived in `GameScene.tryMove` until 2026-08-06, which is why it reads like a
// scene method rather than a rule -- and why it was extracted: this is the
// game's *primary* way anything gets pushed (Transfer/Project·Momentum are the
// cast-based alternatives), and none of it was reachable from a headless test,
// so every bind/push interaction it decides could only ever be checked by
// driving a browser. It also duplicated bind rules the sim already owned,
// which is precisely the drift `design/06_Architecture.md` exists to prevent.
//
// Doors are deliberately NOT handled here: crossing one is a room transition
// with UI attached (a lock panel, `enterRoom`), not a step, so the caller
// checks `Q.doorAt(room, target)` first and never calls this for a door tile
// -- exactly the order `tryMove` already used. A door tile reached anyway
// would simply be walked onto like floor, since `isWalkable` does not treat
// DOOR as solid.
export function walkPlayer(world: WorldState, dir: Facing, out: SimEvents): boolean {
  // **A press turns the caster whether or not it moves them**, and that happens
  // here rather than in the caller so that every caller gets it: the game, a
  // replay, and the analysis search alike.
  //
  // It used to happen only in the game (`GameScene` set `player.facing = dir`
  // before calling this), which made turning a thing the renderer knew about and
  // the simulation did not. The cost of that split was not theoretical: the
  // search treats `face` as a free move, so with facing unset by stepping it
  // produced solutions that turned on the spot to look at an open tile -- which
  // no input can do, because the press that turns you toward an open tile also
  // walks you onto it. Four of the first five library takes were unplayable for
  // exactly that reason (2026-08-15).
  //
  // Set before the walk is decided, so a *refused* press still turns. That is
  // the game's own behaviour ("turning to face a new direction while blocked
  // still changes the target") and it is the only way to aim at something
  // without walking into it.
  world.player.facing = dir;
  const room = world.rooms[world.player.roomId];
  const offset = DIRECTION_OFFSETS[dir];
  const targetX = world.player.x + offset.x;
  const targetY = world.player.y + offset.y;
  const here = (o: ObjectDef) => o.x === targetX && o.y === targetY;

  // Walking into a pushable object shoves it one tile further in the same
  // direction (Sokoban-style) if that tile is free, rather than just blocking
  // like an ordinary object -- purely physical, no cast involved. A
  // bind-linked object is excluded (Q.canPush) -- an independent shove would
  // double-count with its own bind-drag (see queries.canPush's own comment).
  const pushed = room.objects.find((o) => here(o) && Q.canPush(o));
  // A bound-to-caster object sitting directly ahead is a different case
  // from an ordinary push: it is not being pushed at all -- it is simply
  // following the caster's own step, exactly like it would for any other
  // step, and that following is what clears the tile. Q.boundObjectMove is
  // the same pure computation propagateBoundMovement uses to actually move
  // it after the fact; here it is only a pre-check, deciding whether the
  // walk should be allowed through at all, so the two callers can never
  // disagree about where it would land. If its own destination is
  // unavailable, the walk is blocked outright -- there is nowhere for it to
  // go, so the tile it currently occupies is not actually free.
  const boundAhead = room.objects.find((o) => here(o) && o.bind && (o.bind.sourceId ?? 'self') === 'self');
  // A follower bound to a *marked object* (not the caster) sitting directly
  // ahead is neither of the two cases above: canPush excludes it (same
  // reasoning as any other bind follower), and boundAhead only covers the
  // bound-to-caster case. Without this, walking straight into one just
  // blocked outright, same as a wall -- so the only way to move such a
  // follower at all was a cast, when a real physical link should respond to
  // an ordinary shove the same way its source does. Q.pushTargetFor resolves
  // the follower to its own source (one hop only, refusing rather than
  // chasing a chain -- see its own doc comment); redirecting the shove there
  // and letting the existing propagateBoundMovement path drag the follower
  // back is the same fix applyMomentumEffect already has for the cast-based
  // push.
  const boundElsewhereAhead = room.objects.find((o) => here(o) && o.bind && o.bind.sourceId !== undefined);

  if (pushed) {
    const beyondX = targetX + offset.x;
    const beyondY = targetY + offset.y;
    // Deliberately not also checking flowingTiles here -- shoving a
    // pushable object (room9's redirect Boulder) into flowing water is the
    // entire point of that puzzle, so a flowing tile only blocks the
    // player's own feet, never a push destination.
    if (!Q.canObjectEnter(room, beyondX, beyondY, world.physics) || Q.doorAt(room, beyondX, beyondY)) return false;
    moveObjectTo(world, pushed, beyondX, beyondY, out);
    // moveObjectTo already recomputed the water-flow graph above -- if the
    // tile the object just vacated is itself part of that channel, pulling
    // an obstruction out of a flowing tile can turn it flowing again in
    // the same action (the reverse of pushing one in to block it). The
    // object still moves either way; the player just doesn't follow onto
    // ground that's now underwater, the same rule an ordinary walk onto a
    // flowing tile already enforces.
    if (world.flowingTiles.has(`${targetX},${targetY}`)) return false;
  } else if (boundAhead) {
    if (!Q.boundObjectMove(room, boundAhead, targetX, targetY, offset.x, offset.y, world.physics)) return false;
    // Not moved here -- movePlayerTo below re-derives the exact same
    // destination via propagateBoundMovement and actually applies it,
    // dragging boundAhead out of the way as a consequence of the caster's
    // own step landing on its tile, the same as any other step would.
  } else if (boundElsewhereAhead) {
    const push = Q.pushTargetFor(room, boundElsewhereAhead);
    if (!push) return false; // one-hop-only chain, or the resolved source can't move either
    const pushDir = push.mirrored ? Q.oppositeFacing(dir) : dir;
    const pushOffset = DIRECTION_OFFSETS[pushDir];
    const sourceDestX = push.target.x + pushOffset.x;
    const sourceDestY = push.target.y + pushOffset.y;
    // Same exclusions boundObjectMove/applyMomentumEffect's own isBlocked
    // already use: a wall, a door, another solid object, or the caster's
    // own tile (the source isn't necessarily adjacent to, or facing away
    // from, the caster the way an ordinary push target always is).
    if (!Q.canObjectEnter(room, sourceDestX, sourceDestY, world.physics) || Q.doorAt(room, sourceDestX, sourceDestY) || (sourceDestX === world.player.x && sourceDestY === world.player.y)) return false;
    // The source having somewhere to go is not enough on its own --
    // boundElsewhereAhead (directly ahead of the caster right now) needs
    // its *own* destination to be clear too, or propagateBoundMovement
    // leaves it exactly where it is ("breaks lockstep silently") once the
    // source actually moves, and the caster's own step below would walk
    // straight onto a tile that never actually cleared. Same pure
    // precheck the boundAhead branch above already runs, just against the
    // source's *prospective* new position rather than the caster's.
    if (!Q.boundObjectMove(room, boundElsewhereAhead, sourceDestX, sourceDestY, pushOffset.x, pushOffset.y, world.physics)) return false;
    // Moves the source; propagateBoundMovement (inside moveObjectTo) drags
    // boundElsewhereAhead back along with it, clearing the tile the caster
    // is about to step onto -- the same one-hop redirect
    // applyMomentumEffect's own cast-based push already has.
    moveObjectTo(world, push.target, sourceDestX, sourceDestY, out);
  } else if (!Q.isWalkable(room, targetX, targetY, world.physics) || world.flowingTiles.has(`${targetX},${targetY}`)) {
    return false;
  }

  movePlayerTo(world, targetX, targetY, out);
  return true;
}

// Adds a brand-new object to the current room at runtime -- needed by
// Separate·Mass splitting one rock into two (or merging two into one). The
// settle is here rather than folded into addObjectToRoom because a split/merge
// may have just placed a rock straight onto a room8 pressure pad's tile (or
// into room9's water channel): that's a mechanism firing another mechanism, not
// a render refresh.
function addObject(world: WorldState, obj: ObjectDef, out: SimEvents): void {
  addObjectToRoom(world.rooms[world.player.roomId], obj, out);
  settleWorld(world, out);
}

// ---------------------------------------------------------------------------
// Transfer
// ---------------------------------------------------------------------------

// Moves a real quantity between two loci' `properties` -- the mechanism behind
// "use Transfer from a heat source onto an ice-blocked passage to melt it".
// Deliberately generic: any concept, any two loci with `properties`, same rule --
// not a special case keyed on "fireplace + ice". `reversed` (the Reverse
// modifier) flips which locus is drained vs. filled, same as resolve() flips
// caster/target -- just with source/target substituted for caster/target, since
// there's no caster role once a source is marked.
//
// A locus can also be `'self'` (ui.ts defaults an unmarked source to the caster,
// per 03_Rune_Language.md), which reads/writes the caster's own reserves instead
// of an ObjectDef's properties. `energy` and `charge` both have a caster-side
// reserve; any other concept drawn from Self just has nothing available, same
// as an empty object would. This is also how the player actually charges
// themselves up for a Project·Charge discharge (see applyProjectEffect below)
// -- mark a charged object as Source, face somewhere with no object ahead so
// Target defaults to Self, and cast an ordinary Charge Transfer.
export function applyTransferEffect(world: WorldState, event: TransferEffectEvent, out: SimEvents): TransferEffectResult {
  const room = world.rooms[world.player.roomId];
  const targetIsSelf = event.targetObjectId === 'self';
  // A marked source persists across rooms (objectLink.ts), but Transfer only
  // ever acts within the current room -- a source marked elsewhere can never
  // be a real object here, so treating that as "the cast just fails" was a
  // bug: casting with no *usable* source silently did nothing instead of
  // falling back to the caster, the same as if nothing had ever been marked.
  const requestedSource = event.sourceObjectId === 'self' ? undefined : room.objects.find((o) => o.id === event.sourceObjectId);
  const sourceIsSelf = event.sourceObjectId === 'self' || !requestedSource;
  const requestedTarget = targetIsSelf ? undefined : room.objects.find((o) => o.id === event.targetObjectId);

  // **Bind·Entropy displaces** (see sim/entropyBind.ts): a cast naming one end of
  // a link is served by the other, in both roles. Resolved here, at the loci
  // themselves, rather than at the draw and the deposit further down -- so every
  // guard, the draw, the deposit and both `checkMeltThreshold` calls all see the
  // objects actually being acted on, and none of them has to know the link
  // exists. Entropy only: the link says nothing about any other concept, so a
  // Transfer of Energy at a linked object is untouched.
  //
  // Naming *both* ends in one cast is consistent rather than special-cased: the
  // source crosses to the target's tile and the target to the source's, so
  // `C -> B` across a link becomes `B -> C`. Worth knowing before it is met.
  const isEntropy = event.conceptId === 'entropy';
  const source = sourceIsSelf ? undefined : isEntropy ? displacedEntropyLocus(room, requestedSource) : requestedSource;
  const target = isEntropy ? displacedEntropyLocus(room, requestedTarget) : requestedTarget;
  // Checked against the *displaced* target, which is the point: a link whose far
  // end cannot hold Entropy is a cast that fizzles, rather than one that quietly
  // acts on the near end instead.
  if (!targetIsSelf && !target?.properties) return { applied: false };

  const fromIsSelf = event.reversed ? targetIsSelf : sourceIsSelf;
  const toIsSelf = event.reversed ? sourceIsSelf : targetIsSelf;
  const fromObj = event.reversed ? target : source;
  const toObj = event.reversed ? source : target;
  const amount = atScale(world.physics.transfer.amount, event.scale);
  const isEnergy = event.conceptId === 'energy';
  const isCharge = event.conceptId === 'charge';

  // Relocating a generator, rather than moving Charge out of one.
  //
  // The model's rule is that Transfer moves *whatever the marked locus is*:
  // mark a generator and you move the generator, which changes the network's
  // equilibrium permanently, because nothing pulls a generator back. Mark or
  // draw plain Charge and you move Charge, which relaxation undoes. One verb,
  // two things it can be pointed at -- not a per-concept special case.
  //
  // Gated on the target carrying `sourceMount` rather than merely having
  // capacity, and that is not caution: room3's entire puzzle marks the Magnetic
  // Block (a generator) and Transfers Charge into the Sealed Reliquary
  // (capacity 1), so "anywhere that can store" would silently turn a shipped
  // puzzle into a relocation. See ObjectDef.sourceMount.
  //
  // Both halves move together. Moving only the rated output would leave the old
  // vessel holding Charge that its network no longer has an equilibrium for,
  // and the new one climbing to a fresh equilibrium of its own -- Charge
  // created out of an accounting gap. This is picking the battery up, so it
  // goes with what it is holding.
  if (isCharge && !event.reversed && source && target?.sourceMount && source.maxProperties?.charge !== undefined) {
    const rated = source.maxProperties.charge;
    const carried = source.properties?.charge ?? 0;

    delete source.maxProperties.charge;
    if (Object.keys(source.maxProperties).length === 0) delete source.maxProperties;
    if (source.properties) source.properties.charge = 0;

    target.maxProperties = { ...target.maxProperties, charge: rated };
    target.properties = { ...target.properties, charge: (target.properties?.charge ?? 0) + carried };

    settleWorld(world, out);
    return { applied: true, relocatedSource: { sourceName: source.name, targetName: target.name } };
  }

  // Only Energy and Charge have a caster-side reserve to receive into (see
  // the toIsSelf branch below) -- every other concept (Entropy, Mass, Time,
  // Space, Momentum) has nothing on the caster's own side to hold it. Giving
  // is a no-op then, same as an empty object would be, but *drawing* still
  // has to be skipped too: without this guard, a real object source would
  // still be drained (the `else` branch below unconditionally deducts from
  // its properties) even though nothing downstream can hold what was taken,
  // silently destroying it rather than doing nothing. A real, reported bug:
  // room6's Withered Standard/Pristine Reliquary pair holds a single,
  // non-regenerating pocket of Entropy the whole second half of that puzzle
  // depends on -- an accidental self-target (facing empty floor instead of
  // the other relic) destroyed it for good, an unrecoverable softlock.
  if (toIsSelf && !fromIsSelf && !isEnergy && !isCharge) return { applied: false };

  // Neither Energy nor Charge forces a shortfall out of Health when the
  // caster is a locus here -- Charge never has (see player.ts's drawCharge:
  // there's no ambient bioelectric reserve to force from, only whatever the
  // player already charged up), and Energy no longer does either now that the
  // caster's own thermalState has its own, separate danger model (extreme
  // values cost Health via checkThermalDanger, not a forced draw on
  // shortfall -- see drawThermal/giveThermal in player.ts).
  // Both loci must be able to hold the quantity, checked **before anything is
  // drawn**, and the asymmetry this closes is worth naming: the early return
  // near the top checks the *target* carries `properties` and nothing checked
  // the source. A marked source with none reached the draw below as
  // `fromObj!.properties!` and threw, taking the renderer down a long way from
  // the cause.
  //
  // Reachable, and reachable in shipped rooms (found 2026-08-13 by the analysis
  // search, which marks every object in turn). `linkable` in the game is
  // `!!obj.properties || !!obj.pushable`, so every pushable object with no
  // properties of its own is markable: fourteen of them, including the Stone
  // Blocks in room5, both room7 Boulders and room9's redirect Boulder. Mark one
  // -- an entirely natural thing to do, since Bind·Momentum wants exactly that
  // -- then face anything with properties and Transfer.
  //
  // Here rather than at the two use sites, because a guard on the receiving
  // side alone would fire *after* the source had been drained, destroying the
  // quantity instead of moving it. And after the relocation branch above, which
  // legitimately handles a generator carrying `maxProperties` and no
  // `properties` at all.
  //
  // Fizzling is the right answer rather than falling back to the caster: an
  // empty locus has nothing to give and nowhere to put anything, which is what
  // `applied: false` already means everywhere else here.
  if (!fromIsSelf && !fromObj?.properties) return { applied: false };
  if (!toIsSelf && !toObj?.properties) return { applied: false };

  let actual: number;
  if (fromIsSelf) {
    if (isEnergy) {
      actual = drawThermal(world, amount, out);
    } else if (isCharge) {
      actual = drawCharge(world, amount);
    } else {
      actual = 0;
    }
  } else {
    const fromProps = fromObj!.properties!;
    const available = fromProps[event.conceptId] ?? 0;
    actual = Math.min(amount, available);
    fromProps[event.conceptId] = available - actual;
  }

  if (toIsSelf) {
    if (isEnergy) giveThermal(world, actual, out);
    else if (isCharge) giveCharge(world, actual);
  } else {
    const toProps = toObj!.properties!;
    toProps[event.conceptId] = (toProps[event.conceptId] ?? 0) + actual;
  }

  const beforeChecks = out.length;
  const meltedTargetName = !targetIsSelf && target && checkMeltThreshold(room, target, out, world.physics, world.flowingTiles) ? target.name : undefined;
  const meltedSourceName = !sourceIsSelf && source && checkMeltThreshold(room, source, out, world.physics, world.flowingTiles) ? source.name : undefined;
  // A standing pillar's Charge may have just crossed (or dropped below) what a
  // gate pillar's conduitFeed reads live (neither checkMeltThreshold call above
  // touches this -- standing pillars have no meltsWhen of their own), and this
  // Transfer may equally have restored/ruined room9's Aqueduct or drained/
  // refilled a riverbed tile's own Energy, changing the flow topology.
  settleWorld(world, out);
  if (fromIsSelf || toIsSelf) out.push({ kind: 'stats-changed' });
  return {
    applied: true,
    meltedTargetName,
    meltedSourceName,
    stateNotes: stateNotesSince(out, beforeChecks),
    sourceWasSelf: sourceIsSelf,
    // Named only when the link actually moved the cast somewhere else, so the
    // narration can say where it went. Without this a player casts at one end
    // and watches a different object change with nothing to connect the two --
    // which is the whole legibility problem a displacement mechanic has.
    displacedTargetName: target && requestedTarget && target !== requestedTarget ? target.name : undefined,
    displacedSourceName: source && requestedSource && source !== requestedSource ? source.name : undefined,
    moved: actual,
  };
}

/** The `narrates` clauses any state ladder produced since `from` -- see
 * `HeatStateStage.narrates`. Reading them back off the event list is what keeps
 * `checkMeltThreshold`'s return a plain boolean for its twenty other callers,
 * and it cannot drift from what the renderer draws, because it is the same
 * event. */
function stateNotesSince(out: SimEvents, from: number): string[] | undefined {
  const notes = out.slice(from).flatMap((e) => (e.kind === 'state-changed' ? [`${e.objectName} ${e.note}.`] : []));
  return notes.length > 0 ? notes : undefined;
}

// ---------------------------------------------------------------------------
// Transfer · Momentum
// ---------------------------------------------------------------------------

// A Transfer + Momentum cast on a pushable object: shove it away (up to
// tilesRequested, stopping early if something blocks its path), and recoil
// the caster the same distance in the opposite direction -- conservation of
// momentum, not modeled with any per-object mass, so both just move the same
// number of tiles. Costs the caster's own Mana regardless of any marked
// Transfer source (see momentumEffect.ts): Precise/Standard draws that come up
// short just mean a weaker push (drawMana's usual weakens-if-insufficient
// behavior -- if it can't draw the full amount at all, the block doesn't
// budge), but an Unbound push forces the draw through and costs Health on the
// shortfall.
//
// The faced object (whatever event.targetObjectId names) and the object that
// actually moves can now differ: see Q.pushTargetFor. A shove aimed at a
// bind-linked object redirects onto its own bind source instead -- moving
// the source triggers the ordinary propagateBoundMovement path (see
// moveObjectTo), which drags the faced object right back along with it,
// same as if it had been pushed directly. A real, reported bug before this
// existed: a Bind·Momentum link only ever let you push the source end of it,
// when a real physical link should carry force either way. `dir`/`opposite`
// stay anchored to the *faced* object's position, not the redirected one --
// what the caster is actually standing next to and facing is what decides
// which way they recoil, regardless of which object ends up absorbing the
// shove -- and the redirected push itself runs in the opposite direction
// when the link is mirrored, since a mirrored follower's own movement is
// already the inverse of its source's.
export function applyMomentumEffect(world: WorldState, event: MomentumEffectEvent, out: SimEvents): MomentumEffectResult {
  const room = world.rooms[world.player.roomId];
  const facedTarget = room.objects.find((o) => o.id === event.targetObjectId);
  if (!facedTarget) return { applied: false, tilesRequested: 0, tilesMoved: 0 };
  const push = Q.pushTargetFor(room, facedTarget);
  if (!push) return { applied: false, tilesRequested: 0, tilesMoved: 0 };
  const { target, mirrored } = push;

  const dx = facedTarget.x - world.player.x;
  const dy = facedTarget.y - world.player.y;
  const dir: Facing = dx === 1 ? 'right' : dx === -1 ? 'left' : dy === 1 ? 'down' : 'up';
  const opposite = Q.oppositeFacing(dir);
  const pushDir = mirrored ? opposite : dir;

  const tilesRequested = atScale(world.physics.momentum.tiles, event.scale);
  const energyAmount = atScale(world.physics.momentum.energyCost, event.scale);

  let healthCost: number | undefined;
  if (event.scale === 100 && getMana(world) < energyAmount) {
    healthCost = forceDrawMana(world, energyAmount, out);
  } else {
    const drawn = drawMana(world, energyAmount);
    if (drawn < energyAmount) {
      out.push({ kind: 'stats-changed' });
      return { applied: true, tilesRequested, tilesMoved: 0 };
    }
  }
  out.push({ kind: 'stats-changed' });

  // The player's own tile is excluded here too (same reasoning
  // applyMomentumProject's own isBlocked already has): an ordinary,
  // non-redirected push only ever moves target directly away from the
  // caster, so it can never revisit their tile anyway -- but a redirected
  // push (see pushTargetFor above) moves a *different* object, one with no
  // guarantee it starts out adjacent to (or facing away from) the caster at
  // all, so its own path can otherwise cross straight through wherever the
  // caster happens to be standing.
  const isBlocked = (x: number, y: number) => (x === world.player.x && y === world.player.y) || !Q.canObjectEnter(room, x, y) || !!Q.doorAt(room, x, y);
  const blockPath = Q.stepPath(target.x, target.y, pushDir, tilesRequested, isBlocked);
  moveObjectTo(world, target, blockPath.x, blockPath.y, out);

  const recoilPath = Q.stepPath(world.player.x, world.player.y, opposite, tilesRequested, isBlocked);
  movePlayerTo(world, recoilPath.x, recoilPath.y, out);

  // The recoil moves the player, and the shove likely puts real distance
  // between them and whatever they just pushed -- what's ahead of the caster
  // is recomputed by the renderer off the moves reported above.
  return { applied: true, tilesRequested, tilesMoved: blockPath.steps, healthCost };
}

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

// True if anything solid -- a WALL tile, or an object `queries.isSolidForProject`
// calls solid -- occupies a tile strictly between the caster and (toX,toY). A
// standard Bresenham grid walk rather than a continuous raycast, since Linear
// targets are already axis-aligned (one delta is zero) and Radial's own range is
// itself a Manhattan distance, not a true geometric radius, so a diagonal
// approximation is all that metric supports anyway.
//
// **A wall blocks (2026-08-15).** Until then only objects did, on a deliberate
// simplification that let a beam pass through masonry the way `Measure`·`Mass`
// reads through it. It stopped being defensible once `Project`·`Momentum`
// shipped: a heat-ray leaking through a wall is a curiosity, but a *shove*
// through one moves world state from a room you are not in, and there is no
// reading of "force" that survives that. One rule for every concept, since
// what a wall stops is the projection, not the thing being projected.
//
// What still does not block is *absence*: a VOID tile (the room stopping, or a
// hole in its footprint) and an out-of-bounds tile, for exactly the reason
// room2's Chasm objects don't -- an open pit has nothing in it to stop a beam
// on. Nor does a CLIFF tile, which is impassable but is a change of height
// rather than a barrier, and would otherwise stop a beam fired *off* a plateau
// as readily as one fired at its face; a tile id cannot tell the two apart.
export function isProjectPathBlocked(world: WorldState, toX: number, toY: number): boolean {
  // The walk itself moved to `queries.pathBlocked` (2026-08-16), unchanged, so
  // that light could ask the same question from a lamp's tile instead of the
  // caster's. What is left here is the half that is about a *cast*: it starts at
  // the caster's feet, in the caster's room. See that function for why sharing
  // it matters more than the two lines it saves -- a beam and a shadow have to
  // agree about what a wall is.
  return Q.pathBlocked(world.rooms[world.player.roomId], world.player.x, world.player.y, toX, toY);
}

// Project has no target object at all -- it acts on whatever's within range of
// the caster's own position/facing, not a pre-selected locus. That's the whole
// point: it's the first operator a player can use without opening any specific
// object's panel first (see ui.ts's performCast, which fires this regardless of
// which panel happened to be open when Project was cast). Reuses the same
// Precise/Standard/Unbound -> 3/6/10 tile reach as radialMeasure's existing Radial
// visualization, so a modifier means the same range everywhere it appears --
// except Charge's Radial specifically (see the range calculation below).
//
// Bare tile geometry (WALL) still never blocks -- like Measure detecting mass
// through a wall (08_Rune_Combination_Tables.md), that stays a deliberate
// simplification, not an oversight (see 12_Mechanics_World_Interaction.md). Real
// solid matter in the way does block now, though (2026-07-28): any object
// with `blocksProject` set, or a `mass` (real mass is always solid --
// see isProjectPathBlocked), stops the beam/field from reaching anything
// behind it, though it's still hit itself if it's the nearest thing in the
// path. This is why room2's Guttering Candle sits behind Chasm objects
// rather than ordinary Stone Blocks -- an open pit has nothing solid in it
// to stop a beam on, unlike a block would.
export function applyProjectEffect(world: WorldState, event: ProjectEffectEvent, out: SimEvents): ProjectEffectResult {
  const room = world.rooms[world.player.roomId];
  const isEnergy = event.conceptId === 'energy';
  const isCharge = event.conceptId === 'charge';
  const isMomentum = event.conceptId === 'momentum';
  const amount = atScale(world.physics.project.amount, event.scale);
  const reserve = projectReserveFor(event.conceptId);

  // Refused before anything at all happens -- before the FX, before the range
  // scan, before any draw. A concept the caster holds no reserve of is not a
  // weak projection, it is not a projection: nothing is spent, nothing is
  // drawn, and no beam is animated at a wall to suggest otherwise.
  if (!reserve) {
    return { applied: false, hitNames: [], meltedNames: [], noReserve: true };
  }

  // v1 simplification: a Project cast either transmits its full nominal
  // amount or doesn't fire at all -- no silent partial delivery (previously
  // it played the full FX/narration but quietly delivered less than the
  // nominal amount, indistinguishable from a full-strength cast that just
  // wasn't strong enough yet). Unlike Transfer's self-sourced Unbound draw,
  // this never forces the shortfall out of Health: a projected field has
  // only one locus (the caster/source point, see 04_Physics_Engine.md's
  // Project write-up), not a paired second locus to force a deficit
  // through the way Transfer's two loci allow. Flagged as v1-only in
  // 12_Mechanics_World_Interaction.md and 07_Open_Questions.md -- partial
  // delivery with real feedback (and Precise/Unbound actually being reachable
  // in a normal playthrough) is v2 scope.
  const held = reserve === 'mana' ? getMana(world) : getCharge(world);
  if (held < amount) {
    return { applied: false, hitNames: [], meltedNames: [], insufficientReserve: true };
  }
  // A directed discharge (Linear) reaches further than a diffuse field
  // (Radial) can hold together -- Charge's Radial gets its own, shorter
  // table; Linear and every other concept/shape keep the shared one. See
  // 12_Mechanics_Charge.md.
  const range = atScale(isCharge && event.radial ? world.physics.project.chargeRadialRange : world.physics.project.range, event.scale);
  const offset = DIRECTION_OFFSETS[world.player.facing];

  const inRange = room.objects.filter((o) => {
    const dx = o.x - world.player.x;
    const dy = o.y - world.player.y;
    if (dx === 0 && dy === 0) return false;
    if (event.radial) {
      if (Math.abs(dx) + Math.abs(dy) > range) return false;
    } else if (offset.x !== 0) {
      // Linear: straight ahead along whichever axis the caster is facing,
      // same side as travel -- never behind, never off-axis.
      if (!(dy === 0 && Math.sign(dx) === offset.x && Math.abs(dx) <= range)) return false;
    } else {
      if (!(dx === 0 && Math.sign(dy) === offset.y && Math.abs(dy) <= range)) return false;
    }
    return !isProjectPathBlocked(world, o.x, o.y);
  });

  // Purely visual, independent of whether anything was actually there to
  // catch it -- a heat-ray (or a bolt) reads as one even projected at a bare
  // wall or an empty stretch of floor. Which texture belongs to which concept
  // is the same pick it always was, now branching once more for Energy·
  // Reverse's cold-ray; everything else about the animation is the
  // renderer's (see GameScene's animateProjectFx).
  if (isEnergy) out.push({ kind: 'project-fx', texture: event.reversed ? 'fx-frost' : 'fx-flame', radial: event.radial, range, offset });
  else if (isCharge) out.push({ kind: 'project-fx', texture: 'fx-bolt', radial: event.radial, range, offset });
  else if (isMomentum) out.push({ kind: 'project-fx', texture: 'fx-shock', radial: event.radial, range, offset });

  // Momentum diverges completely from here down: it's a physical push/pull
  // on whatever's `pushable`, not a `properties` mutation, so it has its own
  // targets/ordering logic rather than sharing Energy/Charge's below. Cost
  // was already validated and is drawn here since it's shared by both.
  if (isMomentum) {
    drawMana(world, amount);
    out.push({ kind: 'stats-changed' });
    return applyMomentumProject(world, event, inRange, out);
  }

  // Nothing to deliver to means nothing is spent (fixed 2026-08-08). This
  // check used to sit *below* the draw, so a cast at a bare wall drew the
  // caster's reserve and then returned having deposited it nowhere -- the
  // charge or Mana simply left the world. Small enough to go unnoticed for
  // months, and invisible in play because no number is on screen, but it is a
  // real conservation leak and the one the Charge rebuild (see
  // 09_Roadmap.md) cannot be built on top of: that model's whole claim is
  // that charge is conserved and therefore always recoverable, which is also
  // what bounds how badly a room can be softlocked. Purely an ordering fix --
  // `inRange` is computed well above, so `targets` was always derivable here;
  // no new rule, and the "nothing was in range" narration is unchanged.
  //
  // NOTE for step 3 of that rebuild: this filter has to become
  // capacity-aware, not `properties`-aware. Under the network model an object
  // can carry a `properties` bag and still hold nothing (capacity 0 -- a wire,
  // a gate pillar), so "some in-range target belongs to a network with
  // ΣC > 0" is the real condition. Reordering alone leaves a subtler version
  // of the same leak behind.
  const targets = inRange.filter((o) => o.properties);
  const meltedNames: string[] = [];
  const beforeChecks = () => out.length;
  let notesFrom = 0;
  if (targets.length === 0) {
    return { applied: false, hitNames: [], meltedNames: [] };
  }

  // Reserve was already validated above, so this always draws the full
  // amount -- no partial draw possible here anymore. Drawn from whichever
  // reserve `projectReserveFor` named, rather than from a ternary whose false
  // branch quietly meant "Charge" for four concepts that should never have
  // reached it at all.
  const actual = reserve === 'mana' ? drawMana(world, amount) : drawCharge(world, amount);
  out.push({ kind: 'stats-changed' });

  // Conservation fix (2026-07-28): every target used to receive the full
  // `actual` amount independently, so a single cast could deposit
  // `actual * targets.length` total into the world while only costing the
  // caster `actual` once -- free energy out of nowhere the more objects
  // happened to share the beam. Instead the field attenuates as it
  // penetrates: nearest target first, each one (except the last) absorbs
  // half of whatever's left, the last absorbs whatever remains -- the
  // total handed out across every target always sums to exactly `actual`,
  // never more, and a single target in range still gets the full amount
  // (the "last" of one). Order is nearest-to-farthest since that's the
  // order a real beam would actually reach them in.
  const ordered = [...targets].sort((a, b) => {
    const da = Math.abs(a.x - world.player.x) + Math.abs(a.y - world.player.y);
    const db = Math.abs(b.x - world.player.x) + Math.abs(b.y - world.player.y);
    return da - db;
  });
  // Energy·Reverse is a cold-ray, not a second heat-ray: the same amount
  // that a plain Project·Energy would have added, subtracted instead --
  // the "invert the quantity's flow direction" reading Reverse already has
  // under Transfer (source<->target) and Project·Momentum (push<->pull),
  // just applied to the one remaining direction Project·Energy hadn't wired
  // up yet. No floor at 0: a target already colder than the field just gets
  // colder still, the same way an already-hot target giving still just gets
  // hotter under the plain direction -- neither direction clamps today.
  const sign = isEnergy && event.reversed ? -1 : 1;
  let remaining = actual;
  ordered.forEach((obj, i) => {
    const isLast = i === ordered.length - 1;
    const given = isLast ? remaining : Math.floor(remaining / 2);
    remaining -= given;
    obj.properties![event.conceptId] = (obj.properties![event.conceptId] ?? 0) + sign * given;
    if (notesFrom === 0) notesFrom = beforeChecks();
    if (checkMeltThreshold(room, obj, out, world.physics, world.flowingTiles)) meltedNames.push(obj.name);
  });
  // A standing pillar reachable by Project (an emergent alternative to marking
  // the charge source and Transferring) may have just crossed what a gate
  // pillar's conduitFeed reads live -- and a Project·Energy beam that reaches a
  // frozen riverbed tile thaws it, which the water flow reads. The second of
  // those is new: this site used to re-check the gates only, so a thawed tile
  // sat stale until the next push or Transfer. Running the declared order here
  // is what removes that gap.
  settleWorld(world, out);

  return { applied: true, hitNames: targets.map((o) => o.name), meltedNames, stateNotes: stateNotesSince(out, notesFrom) };
}

// Project·Momentum: a force-push beam/field (Reverse: a tractor beam that
// pulls instead), the ranged multi-target counterpart to Transfer·Momentum's
// single adjacent shove-and-recoil. The two stay genuinely distinct rather
// than being two names for the same thing: Transfer moves a quantity between
// exactly two loci and conserves it there, which is *why* the caster recoils
// (the one object shoved is the one thing paying it back); Project spreads a
// quantity outward from a source and costs the caster to project it at all
// (same rule Energy/Charge Project already follow above), with nothing
// coming back the other way -- a beam doesn't yank its own source backward
// the way grabbing one specific block does, so there's no recoil here, and
// it can reach (and move) every pushable object in its path at once, not
// just one.
function applyMomentumProject(world: WorldState, event: ProjectEffectEvent, inRange: ObjectDef[], out: SimEvents): ProjectEffectResult {
  // Cost was already validated and drawn by applyProjectEffect before this
  // was called -- reaching this function at all guarantees the reserve
  // was sufficient, so there's no shortfall case to handle here anymore.
  const room = world.rooms[world.player.roomId];
  // Each thing the beam reaches, resolved to whatever actually has to move for
  // it to move -- itself for an ordinary block, its own bind *source* for a
  // linked follower (Q.pushTargetFor, one hop, exactly as
  // applyMomentumEffect's targeted shove already does).
  //
  // This used to be `inRange.filter(Q.canPush)`, which drops every bind-linked
  // object outright, and that was a real reported bug: a Project·Momentum push
  // propagated source -> follower (the source moves, the link drags the
  // follower along) but never follower -> source, so the same physical link
  // carried force in one direction only -- while both the sokoban walk-into
  // push and Transfer·Momentum redirected through it correctly. An object
  // bound to the *caster* still resolves to nothing and is still skipped;
  // pushTargetFor is deliberately narrow about that (see its own comment).
  type ResolvedHit = { obj: ObjectDef; push: NonNullable<ReturnType<typeof Q.pushTargetFor>> };
  const hits = inRange
    .map((obj) => ({ obj, push: Q.pushTargetFor(room, obj) }))
    .filter((h): h is ResolvedHit => h.push !== null);
  if (hits.length === 0) {
    return { applied: false, hitNames: [], meltedNames: [] };
  }
  const hitNames = hits.map((h) => h.obj.name);

  // A beam wide enough to catch both ends of one link resolves both of them
  // onto the same object, which must still be pushed exactly once -- pushing
  // it per hit would move the pair two tiles for a one-tile cast. The nearest
  // hit wins, since that is the one the beam physically reaches first, and it
  // is its own offset from the caster that decides which way the force points.
  const byDistance = [...hits].sort((a, b) => {
    const da = Math.abs(a.obj.x - world.player.x) + Math.abs(a.obj.y - world.player.y);
    const db = Math.abs(b.obj.x - world.player.x) + Math.abs(b.obj.y - world.player.y);
    return da - db;
  });
  const targets: ResolvedHit[] = [];
  const claimed = new Set<string>();
  for (const hit of byDistance) {
    if (claimed.has(hit.push.target.id)) continue;
    claimed.add(hit.push.target.id);
    targets.push(hit);
  }

  // Same Precise/Standard/Unbound -> 1/2/3 tile table Transfer·Momentum's own
  // shove uses -- a bigger cast pushes/pulls further, same as it costs more.
  const pushTiles = atScale(world.physics.project.momentumTiles, event.scale);
  // The caster's own tile never appears in room.objects (see splitRock's
  // neighbor search for the same gap), so isWalkable alone would happily
  // let a pulled object land right on top of the player -- only matters for
  // Reverse (pushing away can never approach the caster's own tile), but
  // checked unconditionally since it's a correct rule either way.
  const isBlocked = (x: number, y: number) => (x === world.player.x && y === world.player.y) || !Q.canObjectEnter(room, x, y) || !!Q.doorAt(room, x, y);

  // Processing order matters once more than one object is in the beam:
  // pushing away, the farthest one has to clear its own path before the one
  // behind it can move into the space it just left, so farthest goes first;
  // pulling in, it's the opposite -- the nearest one has to move out of the
  // way before the one behind it can come forward.
  const ordered = [...targets].sort((a, b) => {
    const da = Math.abs(a.obj.x - world.player.x) + Math.abs(a.obj.y - world.player.y);
    const db = Math.abs(b.obj.x - world.player.x) + Math.abs(b.obj.y - world.player.y);
    return event.reversed ? da - db : db - da;
  });

  const movedNames: string[] = [];
  for (const { obj, push } of ordered) {
    const dx = obj.x - world.player.x;
    const dy = obj.y - world.player.y;
    // Linear only ever lines up hits along one shared axis to begin with
    // (see inRange's own filter above); Radial can catch something off-axis
    // from the caster, so it pushes/pulls along whichever axis it's
    // furthest offset on -- the same read a single adjacent target already
    // gets trivially in applyMomentumEffect (there, exactly one axis is
    // ever nonzero).
    const awayDir: Facing = Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? 'right' : 'left') : dy >= 0 ? 'down' : 'up';
    const beamDir = event.reversed ? Q.oppositeFacing(awayDir) : awayDir;
    // The force is aimed at whatever the beam hit; a mirrored follower moves
    // opposite its source, so making *it* go that way means moving the source
    // the other way -- the same flip applyMomentumEffect applies to its own
    // redirected shove. For an unlinked object the two are the same object and
    // `mirrored` is false, so this collapses to the original behavior exactly.
    const dir = push.mirrored ? Q.oppositeFacing(beamDir) : beamDir;
    const path = Q.stepPath(push.target.x, push.target.y, dir, pushTiles, isBlocked);
    if (path.steps > 0) {
      moveObjectTo(world, push.target, path.x, path.y, out);
      movedNames.push(obj.name);
    }
  }

  return { applied: true, hitNames, meltedNames: [], movedNames };
}

// ---------------------------------------------------------------------------
// Separate · Mass
// ---------------------------------------------------------------------------

// Separate·Mass: split the faced rock into two of the next tier down
// (Reverse: merge it with an adjacent same-tier rock into one of the tier
// above instead). Structural, not a quantity transfer -- conserves total
// mass either way, so unlike Transfer/Momentum/Project this has no Energy
// cost. The first real effect for Bind/Separate, which every prior room's
// puzzle skipped in favor of Transfer, Momentum, or Project.
export function applySeparateEffect(world: WorldState, event: SeparateEffectEvent, out: SimEvents): SeparateEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target?.mass) return { applied: false, targetName: target?.name ?? '', failureReason: 'not-a-rock' };
  // room9's bridge boulders look and split-check like ordinary rock, but
  // are warded against Mass specifically -- checked before either
  // splitRock or mergeRocks runs, so Reverse can't sidestep it either.
  if (target.wardedConcepts?.includes('mass')) return { applied: false, targetName: target.name, failureReason: 'warded' };

  return event.reversed ? mergeRocks(world, target, out) : splitRock(world, target, out);
}

// What a fresh split/merge piece is holding. Structural identity (name,
// texture, `conduit.conductive`, the `rust` cluster) is rebuilt from
// ROCK_TIER_META, because that is what the tier *is* -- but the quantities the
// pieces carry are conserved rather than reset: each half of a split gets half
// of the original's `properties`, and a merge gets the sum of both parents'.
//
// This replaced a flat `{ entropy: 0 }` on 2026-08-09, when metal's two
// authored readings of "Metal Block" were unified into one splittable object.
// The old reset was defensible while nothing splittable held anything worth
// keeping; it stopped being defensible the moment a conduit fixture sitting in
// a live charge network could be split, because that deleted the Charge it was
// holding -- and Charge is conserved by construction everywhere else in the
// sim (see `chargeBind`'s equalizedValues, and Transfer's own two-loci rule).
// Splitting is not a sink for it. The same argument runs for Entropy in the
// opposite direction: a free reset was a Second Law violation you could cast
// for nothing (Separate·Mass costs no Energy), so a rusted block split and
// merged back came out clean.
//
// Halving is exact, fractions included -- the sim already divides a charge
// network's total across its loci the same way, and `properties` has never
// been an integer quantity. One consequence worth naming: `rustStage` reads an
// *absolute* threshold, so halving can drop a marginally-rusted piece under it
// (a block at exactly `metal.rustEntropy` splits into two clean-reading
// halves). That is the pre-existing tension between an extensive quantity and
// an absolute threshold -- a Pebble and a Boulder rust at the same reading --
// and it is not an exploit, because merging sums the halves straight back over
// the line: there is no way to end up holding a *clean tier-1 block* that was
// rusted before, which is the only thing conduction cares about.
function carriedProperties(sources: ObjectDef[], factor: number, material: 'stone' | 'metal' | 'wood'): ObjectDef['properties'] {
  const carried: Partial<Record<ConceptId, number>> = {};
  for (const source of sources) {
    for (const [key, value] of Object.entries(source.properties ?? {})) {
      const concept = key as ConceptId;
      carried[concept] = (carried[concept] ?? 0) + value * factor;
    }
  }
  // Metal and wood always end up with an Entropy reading even when the piece
  // they came from had none, because rust/rot is read live off exactly that
  // key -- seeded at 0 and then overwritten by anything actually carried.
  const seeded = material === 'stone' ? carried : { entropy: 0, ...carried };
  return Object.keys(seeded).length > 0 ? seeded : undefined;
}

export function splitRock(world: WorldState, target: ObjectDef, out: SimEvents): SeparateEffectResult {
  if (target.mass!.tier === 3) return { applied: false, targetName: target.name, failureReason: 'too-small-to-split' };
  const nextTier = (target.mass!.tier + 1) as 2 | 3;

  // The second piece needs somewhere to go besides the original tile --
  // any free (unwalled, unoccupied) orthogonal neighbor. isWalkable already
  // checks both the tilemap and every current object, so this is the exact
  // same "can something stand here" check movement itself uses; the
  // caster's own tile isn't in room.objects at all, so it's excluded
  // explicitly rather than risking a new rock landing right on top of them.
  const room = world.rooms[world.player.roomId];
  const neighbor = DIRECTIONS.map((dir) => DIRECTION_OFFSETS[dir])
    .map((offset) => ({ x: target.x + offset.x, y: target.y + offset.y }))
    .find((pos) => Q.canObjectEnter(room, pos.x, pos.y) && !(pos.x === world.player.x && pos.y === world.player.y));
  if (!neighbor) return { applied: false, targetName: target.name, failureReason: 'no-room-to-split' };

  const material = target.mass!.material ?? 'stone';
  const meta = ROCK_TIER_META[material][nextTier];
  // Half of whatever the original was holding, to each half of it.
  const pieceProperties = carriedProperties([target], 0.5, material);
  const makePiece = (x: number, y: number): ObjectDef => ({
    x,
    y,
    id: `rock-${world.nextRockId++}`,
    name: meta.name,
    texture: meta.texture,
    spriteScale: meta.spriteScale,
    pushable: true,
    mass: { tier: nextTier, material },
    conduit: meta.conductive ? { conductive: true } : undefined,
    // Metal rusts, wood rots -- stone is the one material on this ladder
    // that doesn't corrode at all. The `rust` cluster itself is structural
    // (which tier's texture pair to read), so it comes from the tier the same
    // way `conduit.conductive` above does; what the object is *holding* is
    // conserved instead -- see carriedProperties.
    rust: material === 'metal' ? { tier: nextTier } : material === 'wood' ? { kind: 'wood-ladder', tier: nextTier } : undefined,
    properties: pieceProperties && { ...pieceProperties },
    interact: { label: 'Inspect', text: meta.description },
  });

  removeObjectFromRoom(room, target, out);
  addObject(world, makePiece(target.x, target.y), out);
  addObject(world, makePiece(neighbor.x, neighbor.y), out);
  return { applied: true, targetName: target.name, resultName: meta.name };
}

export function mergeRocks(world: WorldState, target: ObjectDef, out: SimEvents): SeparateEffectResult {
  if (target.mass!.tier === 1) return { applied: false, targetName: target.name, failureReason: 'already-whole' };
  const room = world.rooms[world.player.roomId];

  // Matches mass.tier (and mass.material -- a stone Rock and a Scrap Metal piece
  // that happen to share a tier never rejoin each other) directly in the
  // position lookup itself, rather than finding "the" object at that tile
  // first and checking its tier after -- room8's pressure pads share a
  // tile with the rock sitting on them, and a plain first-match-by-
  // position lookup would find the (tier-less) pad instead of the rock
  // resting on it, masking a real adjacent partner.
  const targetMaterial = target.mass!.material ?? 'stone';
  const partner = DIRECTIONS.map((dir) => DIRECTION_OFFSETS[dir])
    .map((offset) => room.objects.find((o) => o.x === target.x + offset.x && o.y === target.y + offset.y && o.mass?.tier === target.mass!.tier && (o.mass.material ?? 'stone') === targetMaterial))
    .find((o): o is ObjectDef => !!o);
  if (!partner) return { applied: false, targetName: target.name, failureReason: 'no-partner-to-merge' };

  const prevTier = (target.mass!.tier - 1) as 1 | 2;
  const meta = ROCK_TIER_META[targetMaterial][prevTier];
  const merged: ObjectDef = {
    x: target.x,
    y: target.y,
    id: `rock-${world.nextRockId++}`,
    name: meta.name,
    texture: meta.texture,
    spriteScale: meta.spriteScale,
    pushable: true,
    mass: { tier: prevTier, material: targetMaterial },
    conduit: meta.conductive ? { conductive: true } : undefined,
    rust: targetMaterial === 'metal' ? { tier: prevTier } : targetMaterial === 'wood' ? { kind: 'wood-ladder', tier: prevTier } : undefined,
    // Both parents' readings, summed -- see carriedProperties.
    properties: carriedProperties([target, partner], 1, targetMaterial),
    interact: { label: 'Inspect', text: meta.description },
  };

  removeObjectFromRoom(room, target, out);
  removeObjectFromRoom(room, partner, out);
  addObject(world, merged, out);
  return { applied: true, targetName: target.name, resultName: meta.name };
}

// ---------------------------------------------------------------------------
// Bind / Separate · Momentum
// ---------------------------------------------------------------------------

// Bind·Momentum: couples the faced object's movement to the marked Source
// (or the caster, if nothing is marked -- the same source resolution
// Transfer already uses, right down to a stale cross-room mark falling back
// to the caster) -- every real step that locus takes from here on (see
// movePlayerTo/moveObjectTo's shared propagateBoundMovement) is mirrored onto
// the target, at no Energy cost and ignoring magnitude, the same
// "structural, not a quantity transfer" shape Separate·Mass already has (see
// design/12_Mechanics_Mass_Momentum.md). Reverse at bind time makes the link a
// mirror instead of a shadow: the target moves opposite its source's own
// direction rather than matching it. Re-casting on an already-bound object
// just updates the link -- there is no separate "already bound" failure, the
// same way re-marking a Transfer source is never an error either. Binding an
// object to itself (marked source == faced target) is a no-op, guarded in
// cast.ts before this is ever reached.
//
// Takes no `out`, unlike every other effect here: binding changes nothing a
// renderer would animate (no texture, no position, no reserve) -- only later
// movement does, and that already goes through propagateBoundMovement's own
// `out`.
export function applyBindEffect(world: WorldState, event: BindEffectEvent): BindEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target) return { applied: false, targetName: '', sourceWasSelf: true };
  const requestedSource = event.sourceObjectId === 'self' ? undefined : room.objects.find((o) => o.id === event.sourceObjectId);
  const sourceWasSelf = event.sourceObjectId === 'self' || !requestedSource;
  target.bind = { sourceId: sourceWasSelf ? undefined : requestedSource!.id, mirrored: event.mirrored };
  return { applied: true, targetName: target.name, sourceWasSelf };
}

// Separate·Momentum: releases an existing Bind·Momentum link -- Bind creates
// the coupling, Separate undoes it, the same create/undo shape the operator
// pair already has for Mass (fuse) vs. Mass·Reverse (remerge). A target that
// was never bound reports applied: false rather than silently doing nothing,
// so ui.ts can narrate the distinction. Also takes no `out` -- see
// applyBindEffect above.
export function applyUnbindEffect(world: WorldState, event: UnbindEffectEvent): UnbindEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target?.bind) return { applied: false, targetName: target?.name ?? '' };
  delete target.bind;
  return { applied: true, targetName: target.name };
}

// ---------------------------------------------------------------------------
// Bind / Separate · Energy
// ---------------------------------------------------------------------------

// Bind·Energy: opens a thermal-contact link between the faced object and the
// marked Source (or the caster, if nothing is marked) -- the same source
// resolution every other Bind reading already uses. Unlike applyBindEffect,
// this *does* take `out` and settles immediately: Bind·Momentum has nothing
// to show right after linking (the target only starts following on the
// source's next real move), but Bind·Energy's whole point is an existing gap
// closing, so casting it should visibly nudge both sides right away rather
// than silently waiting for the next unrelated settle-triggering action
// somewhere else in the room. Re-casting on an already-linked object just
// updates it, same as Bind·Momentum. Binding an object to itself is a no-op,
// guarded in cast.ts before this is ever reached.
export function applyEnergyBindEffect(world: WorldState, event: EnergyBindEffectEvent, out: SimEvents): EnergyBindEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target) return { applied: false, targetName: '', sourceWasSelf: true };
  const requestedSource = event.sourceObjectId === 'self' ? undefined : room.objects.find((o) => o.id === event.sourceObjectId);
  const sourceWasSelf = event.sourceObjectId === 'self' || !requestedSource;
  target.energyBind = { sourceId: sourceWasSelf ? undefined : requestedSource!.id, mirrored: event.mirrored };
  settleWorld(world, out);
  return { applied: true, targetName: target.name, sourceWasSelf };
}

// Separate·Energy: releases an existing Bind·Energy link -- the same
// create/undo shape the operator pair already has for Momentum and Mass.
export function applyUnbindEnergyEffect(world: WorldState, event: UnbindEnergyEffectEvent): UnbindEnergyEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target?.energyBind) return { applied: false, targetName: target?.name ?? '' };
  delete target.energyBind;
  return { applied: true, targetName: target.name };
}

// ---------------------------------------------------------------------------
// Bind / Separate · Charge
// ---------------------------------------------------------------------------

// Bind·Charge: strings a wire between the faced object and the marked Source
// (or the caster, if nothing is marked) -- the same source resolution every
// other Bind reading already uses, right down to the self-into-self no-op
// guard in cast.ts. From here on the two loci's `properties.charge` conduct
// to equality the moment either side changes, with whatever crosses dissipating
// as heat on the caster; see ChargeBindDef for the whole mechanic and why the
// tax exists.
//
// Takes `out` and settles immediately, and as of 2026-08-08 that settle *is*
// the conduction: `charge-bind` (chargeBind.ts) is a settle system ordered
// before `conduit-gates`, so declaring the link equalizes both loci totally and
// opens whatever the far end feeds, all inside this one call. See chargeBind.ts
// for why a wire is immediate where Bind·Energy keeps a rate.
//
// Re-casting on an already-wired object just updates the link, same as every
// other Bind -- there is no separate "already bound" failure.
export function applyChargeBindEffect(world: WorldState, event: ChargeBindEffectEvent, out: SimEvents): ChargeBindEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target) return { applied: false, targetName: '', sourceWasSelf: true };
  const requestedSource = event.sourceObjectId === 'self' ? undefined : room.objects.find((o) => o.id === event.sourceObjectId);
  const sourceWasSelf = event.sourceObjectId === 'self' || !requestedSource;
  target.chargeBind = { sourceId: sourceWasSelf ? undefined : requestedSource!.id, mirrored: event.mirrored };
  settleWorld(world, out);
  return { applied: true, targetName: target.name, sourceWasSelf };
}

// Separate·Charge: cuts an existing Bind·Charge wire -- the same create/undo
// shape the operator pair already has for Momentum, Energy and Mass. Each side
// keeps whatever Charge it is holding at that moment, which is what makes
// cutting the wire a real move rather than paperwork: it is how you stop a
// regenerating source from conducting any further, and how you strand a locus
// above a threshold it would otherwise have drained back below.
export function applyUnbindChargeEffect(world: WorldState, event: UnbindChargeEffectEvent): UnbindChargeEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target?.chargeBind) return { applied: false, targetName: target?.name ?? '' };
  delete target.chargeBind;
  return { applied: true, targetName: target.name };
}

// ---------------------------------------------------------------------------
// Bind / Separate · Entropy
// ---------------------------------------------------------------------------

// Bind·Entropy: crosses the faced object and the marked Source over, so that a
// later Transfer of Entropy naming either one is served by the other. See
// EntropyBindDef (types.ts) for the whole mechanic, and `sim/entropyBind.ts`
// for why a displacement -- rather than the equalization Energy and Charge use,
// or the anti-correlation this reading replaced -- is the only thing a link can
// say about two loci's Entropy without either breaking conservation or
// fragmenting the quantity below the thresholds it exists to cross.
//
// Same source resolution and same self-into-self guard (in cast.ts) every
// other Bind reading uses, and re-casting on an already-linked object just
// re-strikes the link rather than failing. The one thing genuinely different
// from its siblings: a caster-sourced link is *inert*, because the caster is
// not an object and holds no Entropy for the far end to stand in for. The link
// is still recorded -- refusing it would make Bind·Entropy the one Bind that
// can fail, and discovering that it does nothing is exactly the kind of thing
// this world expects a player to find out by trying -- but `coupled` says so,
// so ui.ts can narrate a coupling that took hold differently from one that did
// not.
//
// **Writes the cluster and nothing else.** There is no invariant to seed and no
// gap to close: the link changes only where a *future* cast lands, so binding
// two loci that already differ changes neither of them, now or at any settle.
//
// Still takes `out` and settles immediately, like Bind·Charge -- not because
// the link needs settling, but because every cast does: this is where an
// unrelated threshold that was already pending gets its chance to resolve.
export function applyEntropyBindEffect(world: WorldState, event: EntropyBindEffectEvent, out: SimEvents): EntropyBindEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target) return { applied: false, targetName: '', sourceWasSelf: true, coupled: false };
  const requestedSource = event.sourceObjectId === 'self' ? undefined : room.objects.find((o) => o.id === event.sourceObjectId);
  const sourceWasSelf = event.sourceObjectId === 'self' || !requestedSource;
  const coupled = !sourceWasSelf && target.properties?.entropy !== undefined && requestedSource!.properties?.entropy !== undefined;
  target.entropyBind = {
    sourceId: sourceWasSelf ? undefined : requestedSource!.id,
    mirrored: event.mirrored,
  };
  settleWorld(world, out);
  return { applied: true, targetName: target.name, sourceWasSelf, coupled };
}

// Separate·Entropy: severs an existing Bind·Entropy coupling -- the same
// create/undo shape the operator pair already has for Momentum, Energy, Charge
// and Mass. Each side keeps whatever Entropy it holds at that moment, which is
// what makes cutting it a real move: it is how you bank a partner's drained
// state before something else pushes the pair back the other way.
export function applyUnbindEntropyEffect(world: WorldState, event: UnbindEntropyEffectEvent): UnbindEntropyEffectResult {
  const room = world.rooms[world.player.roomId];
  const target = room.objects.find((o) => o.id === event.targetObjectId);
  if (!target?.entropyBind) return { applied: false, targetName: target?.name ?? '' };
  delete target.entropyBind;
  return { applied: true, targetName: target.name };
}
